import { useRef } from 'react'
import { ChevronRight, Clock, Mail, MapPin, Phone } from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from '@/components/ui/item'
import SectionHeading from '@/components/SectionHeading'
import { hotel, whatsappLink } from '@/data/hotel'
import { useGSAP } from '@/lib/gsap'
import { setupReveals } from '@/lib/reveal'

const contactItems = [
  {
    icon: FaWhatsapp,
    label: 'WhatsApp',
    value: hotel.whatsappDisplay,
    href: whatsappLink(),
  },
  {
    icon: Phone,
    label: 'Telefone fixo',
    value: hotel.phoneDisplay,
    href: `tel:+${hotel.phone}`,
  },
  {
    icon: Mail,
    label: 'Email',
    value: hotel.email,
    href: `mailto:${hotel.email}`,
  },
  {
    icon: Clock,
    label: 'Funcionamento',
    value: 'Recepção 24 horas',
  },
  {
    icon: MapPin,
    label: 'Endereço',
    value: hotel.address,
    href: hotel.mapsUrl,
  },
]

function ContactItem({ icon: Icon, label, value, href }) {
  const body = (
    <>
      <ItemMedia className="size-10 rounded-full bg-secondary text-primary">
        <Icon className="size-4" />
      </ItemMedia>
      <ItemContent>
        <ItemDescription>{label}</ItemDescription>
        <ItemTitle className="text-sm break-all sm:break-normal">{value}</ItemTitle>
      </ItemContent>
      {href && (
        <ItemActions>
          <ChevronRight className="size-4 text-muted-foreground" />
        </ItemActions>
      )}
    </>
  )

  if (!href) return <Item variant="muted">{body}</Item>

  const external = href.startsWith('http')
  return (
    <Item variant="muted" asChild>
      <a href={href} {...(external && { target: '_blank', rel: 'noopener noreferrer' })}>
        {body}
      </a>
    </Item>
  )
}

const Contact = () => {
  const root = useRef(null)
  useGSAP(() => setupReveals(root.current), { scope: root })

  return (
    <div ref={root} className="py-20 md:py-24">
      <div className="container-page flex flex-col gap-14">
        <SectionHeading
          as="h1"
          eyebrow="Fale conosco"
          title="Entre em contato"
          description="Estamos aqui para ajudar. Entre em contato conosco através dos canais abaixo."
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card data-reveal className="[--card-spacing:--spacing(6)]">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Informações de contato</CardTitle>
              <CardDescription>Atendimento todos os dias, a qualquer hora.</CardDescription>
            </CardHeader>
            <CardContent>
              <ItemGroup className="gap-2">
                {contactItems.map((item) => (
                  <ContactItem key={item.label} {...item} />
                ))}
              </ItemGroup>
            </CardContent>
            <CardFooter className="border-t-0 bg-transparent pt-0">
              <Button asChild variant="whatsapp" size="xl" className="w-full">
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp data-icon="inline-start" />
                  Conversar agora
                </a>
              </Button>
            </CardFooter>
          </Card>

          <Card data-reveal className="min-h-[420px] py-0">
            <iframe
              title="Localização do Star Hotel"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3952.231727313775!2d-35.44597777073087!3d-7.8708037748635!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7abbc791610bd87%3A0x8512bda157e8b879!2sStar%20Hotel%20Limoeiro!5e0!3m2!1spt-BR!2sbr!4v1744216919624!5m2!1spt-BR!2sbr"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="size-full min-h-[420px] flex-1 border-0"
            />
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Contact
